import type { Currency, SearchAPIParams } from '@/domainLogic/index.ts';
import { Language, removeUndefinedValues, SearchModes } from '@/domainLogic/index.ts';
import type { FlightClassType } from '@/app/components/search/utils/FlightClassType.ts';
import { isLeisure } from '@/app/components/search/utils/searchTypesUtils.ts';
import { toYYYYMMDD } from '@/app/components/search/utils/misc.ts';
import { getLastDayOfMonth, isNil, isNilString, isValidDate } from '@/app/components/search/Utilities.ts';
import { createOffsetFromPage } from '@/app/components/search/utils/pagerUtils.ts';
import type { SortValues } from '@/app/common/memsearch/types.ts';
import { getMemSearchSearchMode } from '@/app/common/memsearch/getMemsearchEndPoint.ts';
import type { SearchAPIParamsWithCertificate } from '@/app/common/sustainabilityCertificate.ts';

// TODO Define group names properly!
export enum SEARCH_GROUP {
  GROUP_0 = 0,
  GROUP_2 = 2,
  GROUP_5 = 5, // GROUP_VIEW_BY_DEPARTURE_RETURN_DATES
}

export type Coordinate = {
  lng: number;
  lat: number;
};

export enum SOURCE {
  OPENX = 'openx',
  TSX = 'tsx',
  TOUR_GPT = 'tourgpt',
}

export enum BUDGET_FILTER_MODE {
  TOTAL_PRICE = 0,
  PER_PERSON = 1,
  PER_NIGHT = 2,
}

export type CreateMemSearchParams = {
  searchMode: SearchModes;
  origin: string;
  destination: string;
  dateFrom: string;
  dateTo: string;
  adults: string;
  childrenAges: string;
  sort: SortValues;
  mapArea: string; // TODO: Change this to structured params
  source: SOURCE;
  lang?: Language;
  currency?: Currency;
  loggedIn?: boolean;
  northWest?: Coordinate;
  southEast?: Coordinate;
  daysToAdd?: number;
  isEmployeeMode?: boolean;
  page?: number;
  offset?: number;
  group?: SEARCH_GROUP;
  flightClass?: FlightClassType;
  limit?: number;
  hotelId?: number;
  highlightHotelId?: number;
  bp?: string;
  stateful?: number;
  vcSearchId?: string;
  userId?: string;
  meal?: string;
  price?: string;
  maxPrice?: number;
  direct?: boolean;
  iand?: boolean;
  freeCancellation?: boolean;
  hotelStars?: string;
  rating?: string;
  flashSales?: boolean;
  myPrice?: boolean;
  tsh?: boolean;
  leisure?: string;
  leisureType?: string;
  provider?: string;
  filterByProvider?: string;
  accommodationType?: string;
  accommodationFacilities?: string;
  facilities?: string;
  accommodationChain?: string;
  accommodationDistrict?: string;
  accommodationTheme?: string;
  accommodationName?: string;
  accommodationStation?: string;
  accommodationSkiLift?: string;
  accommodationBeach?: string;
  filterBlueFlag?: boolean;
  filterSustainabilityCertificate?: boolean;
  filterBeachType?: string;
  filterClimate?: boolean;
  filterFauna?: string;
  filterWatersport?: string;
  filterEntertainments?: string;
  filterWaterwaves?: string;
  noContent?: boolean;
  prefilterAirlines?: string;
  prefilterDestinationAirport?: string;
  prefilterTransportId?: string;
  prefilterUniversalTransportId?: string;
  plusProposal?: boolean;
  overnightFlight?: boolean;
  consumePriceDecrease?: boolean;
  mobileRates?: boolean;
  disableRadiusSearch?: boolean;
  dynamicRanking?: boolean;
  isOpxEnabled?: boolean;
  bookNowPayLater?: boolean;
  filterVoucherExtra?: boolean;
  customCheckinCheckout?: string;
  packageDiscountEnabled?: boolean;
  opxFlexible?: boolean;
  filterOpxFlexible?: boolean;
  rtcoApplyRanking?: boolean;
  rtcoDisableRanking?: boolean;
  rtcoVariation?: number;
  feedContext?: string;
  geodbLiveCountrySearch?: boolean;
  budgetFilterMode?: number;
  limitedInventoryTest?: boolean;
  hybridSearch?: boolean;
  instantSearch?: boolean;
  taxesIncludedInPrice?: boolean;
  urn?: string;
  filterLoyaltyDeals?: boolean;
  isMobileDevice: boolean;
  inventories?: string;
  excludedInventories?: string;
  enforceApex: boolean;
  apexAllowVCFallback: boolean;
  apexFlightsCheapest: boolean;
  apexCacheMaxAge?: number;
  apexBestScoreTolerance?: number;
  apexFlexibleDays?: number;
};

export const createDestinationParam = ({
  leisureType,
  leisure,
  destination,
}: {
  leisureType?: string;
  leisure?: string;
  destination: string;
}) => {
  if (isLeisure(destination)) {
    return { destination: 'A', leisure: destination.replace(/L/g, '') };
  }
  if (leisureType) {
    return {
      destination: decodeURIComponent(destination),
      leisure: leisureType.replace(/L/g, ''),
    };
  }
  return { destination: decodeURIComponent(destination), leisure: !isNilString(leisure) ? leisure : undefined };
};

const removeDays = (date: string, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() - days);
  return newDate;
};

const addDays = (date: string, days: number) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

// TODO: It would be nice to refactor this... :-)
export const createDatesParams = (adddays: number | undefined, datefrom: string, dateto: string) => {
  const queryResponse = {
    adddays,
    datefrom,
    dateto,
  };

  if (queryResponse.adddays && isValidDate(new Date(queryResponse.datefrom))) {
    const departureDate = new Date(queryResponse.datefrom);
    // @ts-ignore
    const minDepartureDate = removeDays(queryResponse.datefrom, parseInt(adddays.toString(), 10));
    // @ts-ignore
    const maxDepartureDate = addDays(queryResponse.datefrom, parseInt(adddays.toString(), 10));

    const returnDate = new Date(queryResponse.dateto);
    const diffInTime = returnDate.getTime() - departureDate.getTime();
    const diffInDays = diffInTime / (1000 * 3600 * 24);

    queryResponse.datefrom = `${toYYYYMMDD(minDepartureDate)},${toYYYYMMDD(maxDepartureDate)}`;
    queryResponse.dateto = `T,${diffInDays},${diffInDays}`;
  }
  if (queryResponse.datefrom && queryResponse.datefrom.indexOf('M,') !== -1) {
    // @ts-ignore
    const months = queryResponse.datefrom.toString().split('M,')[1].split(',');
    const monthsLength = months.length;
    if (monthsLength === 1) {
      // @ts-ignore
      const year = parseInt(months[0].split('-')[0], 10);
      // @ts-ignore
      const month = parseInt(months[0].split('-')[1], 10);
      const lastDayOfMonth = getLastDayOfMonth({ year, month });
      queryResponse.datefrom = `${months[0]}-01,${months[0]}-${lastDayOfMonth}`;
    } else {
      // @ts-ignore
      const year = parseInt(months[monthsLength - 1].split('-')[0], 10);
      // @ts-ignore
      const month = parseInt(months[monthsLength - 1].split('-')[1], 10);
      const lastDayOfMonth = getLastDayOfMonth({ year, month });
      queryResponse.datefrom = `${months[0]}-01,${months[monthsLength - 1]}-${lastDayOfMonth}`;
    }
  }

  return { datefrom: queryResponse.datefrom, dateto: queryResponse.dateto };
};

export const createOriginValue = (searchMode: SearchModes, origin?: string): string | undefined =>
  searchMode !== SearchModes.HO ? origin : undefined;

const createStatefulParams = (stateful?: number, vcSearchId?: string, userId?: string) => {
  if (isNil(stateful)) return {};
  const vcSearchIdParam = !isNilString(vcSearchId) ? { search_id: vcSearchId } : {};
  const userIdParam = !isNilString(userId) ? { user_id: userId } : {};
  return {
    stateful,
    ...vcSearchIdParam,
    ...userIdParam,
  };
};

const createFilterCoordinatesParam = (mapArea: string, northWest?: Coordinate, southEast?: Coordinate) => {
  if (!isNil(mapArea)) return { filter_coordinates: mapArea.replace('MAPSEARCH,', '') };
  if (!isNil(northWest) && !isNil(southEast) && northWest && southEast)
    return { filter_coordinates: `${northWest.lat},${northWest.lng},${southEast.lat},${southEast.lng}` };
  return {};
};

const createPriceParams = (price: string | undefined, maxPrice: number | undefined, dateFrom: string) => {
  if (price !== undefined && !isNil(maxPrice) && isValidDate(new Date(dateFrom))) {
    const priceRange = price.split(',');
    const min = parseInt(priceRange[0] as string, 10);
    const max = parseInt(priceRange[1] as string, 10);

    if (max === maxPrice) return { budget: `${min},0` };
    return { budget: price };
  }
  if (price !== undefined) {
    return { price };
  }
  return {};
};

const getIncludedTaxes = ({ taxesIncludedInPrice }: { taxesIncludedInPrice?: boolean }) => {
  if (taxesIncludedInPrice === undefined) return undefined;
  return taxesIncludedInPrice ? 1 : 0;
};

export const createMemSearchParams = ({
  lang,
  currency,
  searchMode,
  origin,
  destination,
  daysToAdd,
  dateFrom,
  dateTo,
  adults,
  childrenAges,
  isEmployeeMode = false,
  sort,
  page,
  offset,
  group = SEARCH_GROUP.GROUP_0,
  flightClass,
  limit,
  hotelId,
  highlightHotelId,
  bp,
  stateful,
  vcSearchId,
  userId,
  loggedIn,
  mapArea,
  northWest,
  southEast,
  source,
  meal,
  price,
  maxPrice,
  direct = false,
  iand = false,
  freeCancellation = false,
  hotelStars,
  rating,
  flashSales = false,
  myPrice = false,
  tsh = false,
  leisure,
  leisureType,
  provider,
  filterByProvider,
  accommodationType,
  accommodationFacilities,
  facilities,
  accommodationDistrict,
  accommodationChain,
  accommodationTheme,
  accommodationName,
  accommodationStation,
  accommodationSkiLift,
  accommodationBeach,
  filterBlueFlag,
  filterSustainabilityCertificate,
  filterBeachType,
  filterClimate,
  filterFauna,
  filterWatersport,
  filterEntertainments,
  filterWaterwaves,
  noContent,
  prefilterAirlines,
  prefilterDestinationAirport,
  prefilterTransportId,
  prefilterUniversalTransportId,
  plusProposal = false,
  consumePriceDecrease = false,
  overnightFlight = false,
  mobileRates = false,
  disableRadiusSearch = false,
  dynamicRanking = false,
  isOpxEnabled = false,
  bookNowPayLater = false,
  filterVoucherExtra = false,
  customCheckinCheckout,
  packageDiscountEnabled = false,
  opxFlexible = false,
  filterOpxFlexible = false,
  rtcoApplyRanking = false,
  rtcoDisableRanking = false,
  rtcoVariation,
  feedContext,
  geodbLiveCountrySearch,
  budgetFilterMode = searchMode === SearchModes.DP ? BUDGET_FILTER_MODE.PER_PERSON : undefined,
  limitedInventoryTest = false,
  hybridSearch = false,
  instantSearch = false,
  taxesIncludedInPrice,
  urn,
  filterLoyaltyDeals = false,
  isMobileDevice = false,
  inventories,
  excludedInventories,
  enforceApex = false,
  apexAllowVCFallback = false,
  apexFlightsCheapest = false,
  apexCacheMaxAge,
  apexBestScoreTolerance,
  apexFlexibleDays,
}: CreateMemSearchParams): SearchAPIParams => {
  const destinationParam = createDestinationParam({ leisureType, leisure, destination });
  const datesParams = createDatesParams(daysToAdd || 0, dateFrom, dateTo);
  const isMapSearchParams = group === SEARCH_GROUP.GROUP_0 ? { is_map_search: 1, enable_alternative_dates: 1 } : {};
  const stateFulParams = createStatefulParams(stateful, vcSearchId, userId);
  const filterCoordinatesParam = createFilterCoordinatesParam(mapArea, northWest, southEast);
  const priceParams = createPriceParams(price, maxPrice, datesParams.datefrom);
  const prefilterDirectFlights = direct && isValidDate(new Date(datesParams.datefrom));
  const oldOpxLogic = lang === Language.DE;
  const opxParam = isOpxEnabled === undefined ? oldOpxLogic : isOpxEnabled;

  const paramsFull: SearchAPIParamsWithCertificate = {
    search_mode: getMemSearchSearchMode(searchMode),
    origin: createOriginValue(searchMode, origin),
    ...destinationParam,
    ...datesParams,
    adults,
    ch_ages: childrenAges || undefined,
    lang: lang && lang.toString().toLowerCase(),
    crn: currency,
    sort,
    offset: createOffsetFromPage(page || 1, offset ? `${offset}` : undefined),
    group: group || undefined,
    ...isMapSearchParams,
    flight_class: !isNil(flightClass) ? flightClass : undefined,
    limit: !isNil(limit) ? limit : undefined,
    hotel_id: !isNil(hotelId) ? hotelId : undefined,
    highlight_hotel_id: !isNil(highlightHotelId) ? highlightHotelId : undefined,
    bp: !isNilString(bp) ? bp : undefined,
    ...stateFulParams,
    user_is_authorized: loggedIn ? 1 : undefined,
    user_is_employee: isEmployeeMode ? 1 : undefined,
    ...filterCoordinatesParam,
    incl_taxes: getIncludedTaxes({ taxesIncludedInPrice }),
    meal: !isNilString(meal) ? meal : undefined,
    ...priceParams,
    direct: direct ? 1 : undefined,
    prefilter_direct_flights: prefilterDirectFlights ? 1 : undefined,
    iand: iand ? 1 : undefined,
    free_cancellation: freeCancellation ? 1 : undefined,
    hotel_stars: !isNilString(hotelStars) ? hotelStars : undefined,
    rating: !isNilString(rating) ? rating : undefined,
    flash_sales: flashSales ? 1 : undefined,
    myprice: myPrice ? 1 : undefined,
    tsh: tsh ? 1 : undefined,
    allowed_providers: !isNilString(provider) ? provider : undefined,
    allowed_providers_on_selected: !isNilString(filterByProvider) ? filterByProvider : undefined,
    accomodation_type: !isNilString(accommodationType) ? accommodationType : undefined,
    accomodation_facilities: !isNilString(accommodationFacilities) ? accommodationFacilities : undefined,
    facilities: !isNilString(facilities) ? facilities : undefined,
    accomodation_district: !isNilString(accommodationDistrict) ? accommodationDistrict : undefined,
    accomodation_chain: !isNilString(accommodationChain) ? accommodationChain : undefined,
    accomodation_theme: !isNilString(accommodationTheme) ? accommodationTheme : undefined,
    accomodation_name: !isNilString(accommodationName) ? accommodationName : undefined,
    accomodation_station: !isNilString(accommodationStation) ? accommodationStation : undefined,
    accomodation_skilift: !isNilString(accommodationSkiLift) ? accommodationSkiLift : undefined,
    accomodation_beach: !isNilString(accommodationBeach) ? accommodationBeach : undefined,
    filter_blue_flag: filterBlueFlag ? 1 : undefined,
    sustainability_certificate: filterSustainabilityCertificate ? 1 : undefined,
    filter_beach_type: !isNilString(filterBeachType) ? filterBeachType : undefined,
    filter_climate: filterClimate ? 1 : undefined,
    filter_fauna: !isNilString(filterFauna) ? filterFauna : undefined,
    filter_watersport: !isNilString(filterWatersport) ? filterWatersport : undefined,
    filter_entertainments: !isNilString(filterEntertainments) ? filterEntertainments : undefined,
    filter_waterwaves: !isNilString(filterWaterwaves) ? filterWaterwaves : undefined,
    opx: opxParam ? 1 : undefined,
    plus_rates: plusProposal ? 1 : undefined,
    nocontent: noContent ? 1 : undefined,
    source,
    budget_filter_mode: budgetFilterMode,
    prefilter_airlines: !isNilString(prefilterAirlines) ? prefilterAirlines : undefined,
    prefilter_destination_airport:
      !isNilString(prefilterDestinationAirport) && prefilterDestinationAirport !== '0'
        ? prefilterDestinationAirport
        : undefined,
    prefilter_transport_id: !isNilString(prefilterTransportId) ? prefilterTransportId : undefined,
    prefilter_universal_transport_id: !isNilString(prefilterUniversalTransportId)
      ? prefilterUniversalTransportId
      : undefined,
    consume_price_decrease: consumePriceDecrease ? 1 : undefined,
    overnight_flights: overnightFlight ? 1 : undefined,
    mobile_rates: mobileRates ? 1 : undefined,
    dynamic_ranking: dynamicRanking ? 1 : undefined,
    disable_radius_search: disableRadiusSearch ? 1 : undefined,
    book_now_pay_later: bookNowPayLater ? 1 : undefined,
    filter_voucher_extra: filterVoucherExtra ? 1 : undefined,
    prefilter_checkin_checkout: !isNilString(customCheckinCheckout) ? customCheckinCheckout : undefined,
    package_discount: packageDiscountEnabled ? 1 : undefined,
    opx_flexible: opxFlexible ? 1 : undefined,
    filter_opx_flexible: filterOpxFlexible ? 1 : undefined,
    enable_rtco: rtcoApplyRanking ? 1 : undefined,
    disable_rtco: rtcoDisableRanking ? 1 : undefined,
    rtco_variation: !isNil(rtcoVariation) ? rtcoVariation : undefined,
    feedContext: !isNil(feedContext) ? feedContext : undefined,
    geodb_live_country_search: geodbLiveCountrySearch ? 1 : undefined,
    limited_inventory_test: limitedInventoryTest ? 1 : undefined,
    hybrid_search: hybridSearch ? 1 : undefined,
    instant_search: instantSearch ? 1 : undefined,
    user_loyalty_urn: urn || undefined,
    filter_loyalty_deals: filterLoyaltyDeals ? 1 : undefined,
    is_mobile_device: isMobileDevice ? 1 : undefined,
    inventories,
    excluded_inventories: excludedInventories,
    enforce_apex: enforceApex ? 1 : undefined,
    apex_allow_vc_fallback: apexAllowVCFallback ? 1 : undefined,
    apex_flights_cheapest: apexFlightsCheapest ? 1 : undefined,
    apex_cache_max_age: (apexCacheMaxAge ?? 0) > 0 ? apexCacheMaxAge : undefined,
    apex_best_score_tolerance: !isNil(apexBestScoreTolerance) ? apexBestScoreTolerance : undefined,
    apex_flexible_days: (apexFlexibleDays ?? 0) > 0 ? apexFlexibleDays : undefined,
  };

  return removeUndefinedValues(paramsFull);
};
